// src/pages/ForgotPasswordFlow.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Container,
  Divider,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import axios from "../api/axios";
import { useNavigate } from "react-router-dom";

const AGENCY_NAME = "Dieu est notre Force";
const CODE_EXPIRY_MINUTES = 10;
const RESEND_COOLDOWN_SECONDS = 60;

const steps = ["Email", "Code", "Nouveau mot de passe"];

const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const maskEmail = (value) => {
  const email = String(value || "").trim();
  if (!email.includes("@")) return email;
  const [name, domain] = email.split("@");
  const maskedName =
    name.length <= 2 ? `${name[0] || ""}*` : `${name.slice(0, 2)}***`;
  const domainParts = domain.split(".");
  const maskedDomain =
    domainParts.length >= 2
      ? `${domainParts[0].slice(0, 2)}***.${domainParts.slice(1).join(".")}`
      : `${domain.slice(0, 2)}***`;
  return `${maskedName}@${maskedDomain}`;
};

const parseApiError = (err) => {
  const data = err?.response?.data;
  if (data?.errors?.length) return data.errors.map((e) => e.msg).join("\n");
  if (data?.message) return data.message;
  return "Une erreur s'est produite. Réessayez dans quelques instants.";
};

export default function ForgotPasswordFlow() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0); // 0,1,2
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  const [alert, setAlert] = useState({ type: "info", text: "" });
  const [loading, setLoading] = useState(false);

  const [cooldown, setCooldown] = useState(0);
  const [expiresAt, setExpiresAt] = useState(null);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const remainingSeconds = useMemo(() => {
    if (!expiresAt) return 0;
    const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
    return Math.max(0, diff);
  }, [expiresAt]);

  const remainingLabel = useMemo(() => {
    const m = Math.floor(remainingSeconds / 60);
    const s = remainingSeconds % 60;
    return `${m} min ${String(s).padStart(2, "0")} sec`;
  }, [remainingSeconds]);

  const emailError =
    email.length > 0 && !isValidEmail(email)
      ? "Veuillez entrer une adresse email valide."
      : "";

  const codeError =
    code.length > 0 && code.trim().length < 4 ? "Le code semble incomplet." : "";

  const passwordError =
    newPassword.length > 0 && newPassword.length < 8
      ? "Le mot de passe doit contenir au moins 8 caractères."
      : "";

  const canSendCode = isValidEmail(email) && !loading;
  const canVerifyCode =
    isValidEmail(email) && code.trim().length >= 4 && !loading && remainingSeconds > 0;
  const canReset =
    isValidEmail(email) && code.trim().length >= 4 && newPassword.length >= 8 && !loading;

  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setAlert({ type: "success", text: "Code copié." });
    } catch {
      setAlert({ type: "warning", text: "Impossible de copier le code." });
    }
  };

  const handleRequestCode = async () => {
    setAlert({ type: "info", text: "" });
    setLoading(true);

    try {
      await axios.post("/request-reset-code", { email: email.trim() });

      // ✅ Message aligné avec Option 2 (enterprise/premium), neutre, pro
      setAlert({
        type: "success",
        text:
          "Une demande de réinitialisation a été initiée.\n\n" +
          "Si un compte existe avec cette adresse, un code de vérification a été envoyé par email.\n" +
          `Expiration : ${CODE_EXPIRY_MINUTES} minutes\n\n` +
          "Pour votre sécurité, ne partagez jamais ce code. Notre équipe ne vous le demandera jamais.\n\n" +
          `— ${AGENCY_NAME}`,
      });

      setStep(1);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setExpiresAt(Date.now() + CODE_EXPIRY_MINUTES * 60 * 1000);
    } catch (err) {
      // Pro + clair
      setAlert({ type: "error", text: parseApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (cooldown > 0) return;
    await handleRequestCode();
  };

  const handleVerifyCode = async () => {
    setAlert({ type: "info", text: "" });
    setLoading(true);

    try {
      await axios.post("/verify-reset-code", { email: email.trim(), code: code.trim() });

      setAlert({
        type: "success",
        text:
          "Code confirmé.\n\n" +
          "Veuillez définir un nouveau mot de passe pour sécuriser votre compte.\n\n" +
          `— ${AGENCY_NAME}`,
      });

      setStep(2);
    } catch (err) {
      setAlert({
        type: "error",
        text: err?.response?.status === 400
          ? "Code invalide ou expiré. Veuillez vérifier le code ou demander un nouveau code."
          : parseApiError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setAlert({ type: "info", text: "" });
    setLoading(true);

    try {
      await axios.post("/reset-password", {
        email: email.trim(),
        code: code.trim(),
        newPassword,
      });

      setAlert({
        type: "success",
        text: `Mot de passe réinitialisé avec succès.\nRedirection vers la connexion...\n\n— ${AGENCY_NAME}`,
      });

      setTimeout(() => navigate("/login"), 1400);
    } catch (err) {
      setAlert({ type: "error", text: parseApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 6, mb: 6 }}>
        <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
            Réinitialisation du mot de passe
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Recevez un code, vérifiez-le, puis choisissez un nouveau mot de passe.
          </Typography>

          <Stepper activeStep={step} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {alert.text ? (
            <Alert severity={alert.type} sx={{ mb: 2, whiteSpace: "pre-line" }}>
              {alert.text}
            </Alert>
          ) : null}

          {/* STEP 1 */}
          {step === 0 && (
            <>
              <TextField
                label="Adresse email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                autoFocus
                error={Boolean(emailError)}
                helperText={emailError || " "}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2 }} />

              <LoadingButton
                variant="contained"
                fullWidth
                loading={loading}
                onClick={handleRequestCode}
                disabled={!canSendCode}
                sx={{ py: 1.2, borderRadius: 2 }}
              >
                Envoyer le code
              </LoadingButton>

              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Vérifiez aussi le dossier Spam/Indésirables.
              </Typography>
            </>
          )}

          {/* STEP 2 */}
          {step === 1 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Code envoyé à : <b>{maskEmail(email)}</b>
              </Typography>

              {remainingSeconds > 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Validité du code : {remainingLabel}
                </Typography>
              ) : (
                <Typography variant="caption" color="warning.main" sx={{ display: "block", mb: 1 }}>
                  Le code a expiré. Veuillez renvoyer un nouveau code.
                </Typography>
              )}

              <TextField
                label="Code de vérification"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                error={Boolean(codeError)}
                helperText={codeError || " "}
                inputProps={{ inputMode: "numeric" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ContentCopyIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={copyCodeToClipboard} edge="end" size="small" aria-label="copy code">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2 }} />

              <LoadingButton
                variant="contained"
                fullWidth
                loading={loading}
                onClick={handleVerifyCode}
                disabled={!canVerifyCode}
                sx={{ py: 1.2, borderRadius: 2 }}
              >
                Vérifier le code
              </LoadingButton>

              <LoadingButton
                variant="text"
                fullWidth
                loading={loading}
                onClick={handleResendCode}
                disabled={cooldown > 0 || loading}
                sx={{ mt: 1, borderRadius: 2 }}
              >
                {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : "Renvoyer le code"}
              </LoadingButton>

              <LoadingButton
                variant="outlined"
                fullWidth
                onClick={() => {
                  setStep(0);
                  setAlert({ type: "info", text: "" });
                }}
                disabled={loading}
                sx={{ mt: 1, borderRadius: 2 }}
              >
                Changer l’email
              </LoadingButton>
            </>
          )}

          {/* STEP 3 */}
          {step === 2 && (
            <>
              <TextField
                label="Nouveau mot de passe"
                type={showPass ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                autoFocus
                error={Boolean(passwordError)}
                helperText={passwordError || " "}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass((v) => !v)} edge="end" size="small">
                        {showPass ? (
                          <VisibilityOffOutlinedIcon fontSize="small" />
                        ) : (
                          <VisibilityOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Divider sx={{ my: 2 }} />

              <LoadingButton
                variant="contained"
                fullWidth
                loading={loading}
                onClick={handleResetPassword}
                disabled={!canReset}
                sx={{ py: 1.2, borderRadius: 2 }}
              >
                Réinitialiser le mot de passe
              </LoadingButton>

              <LoadingButton
                variant="text"
                fullWidth
                onClick={() => setStep(1)}
                disabled={loading}
                sx={{ mt: 1, borderRadius: 2 }}
              >
                Retour au code
              </LoadingButton>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
